import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatAmd, resolveAppLocale } from '../locale.js';
import type { Database } from '@cafe-pos/supabase';
import { supabase } from '../supabase.js';
import { KeyRound, Pencil } from 'lucide-react';
import {
  Button,
  Callout,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Field,
  FormGrid,
  PageHeader,
  RowMenu,
  Stat,
  Stats,
  StatusPill,
} from '../ui/index.js';
import { ClientStatsSheet } from './ClientStatsSheet.js';
import {
  daysSince,
  freshness,
  loadProviderSnapshot,
  registerSummary,
  type ProviderSnapshot,
} from './clientStats.js';

type ClientRow = Database['public']['Tables']['clients']['Row'];

interface Credentials {
  email: string;
  password: string;
  clientName: string;
}

/** Wallet passes issued per client (members with wallet_serial). */
type PassCounts = Record<string, number>;
type LoyaltyFlags = Record<string, boolean>;

export function ClientsManager() {
  const { t, i18n } = useTranslation();
  const locale = resolveAppLocale(i18n.language);
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [busy, setBusy] = useState(false);
  const [creds, setCreds] = useState<Credentials | null>(null);

  // inline rename
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  // owner emails: clientId → email
  const [ownerEmails, setOwnerEmails] = useState<Record<string, string>>({});
  const [passCounts, setPassCounts] = useState<PassCounts>({});
  const [loyaltyFlags, setLoyaltyFlags] = useState<LoyaltyFlags>({});
  const [snapshot, setSnapshot] = useState<ProviderSnapshot | null>(null);
  const [statsClientId, setStatsClientId] = useState<string | null>(null);

  // per-row reset state: clientId → { busy, confirming, result }
  const [resetState, setResetState] = useState<
    Record<
      string,
      { busy: boolean; confirming: boolean; creds: { email: string; password: string } | null }
    >
  >({});

  async function load() {
    if (!supabase) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) setError(error.message);
    else {
      setClients(data);
      void refreshStats(data.map((c) => c.id));
    }
    setLoading(false);
  }

  async function refreshStats(ids: string[]) {
    if (!supabase) return;
    try {
      setSnapshot(await loadProviderSnapshot(supabase, ids));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function loadOwnerEmails() {
    if (!supabase) return;
    const { data, error } = await supabase.functions.invoke('get-client-owners', {
      method: 'GET',
    });
    if (error || !data) return;
    const map: Record<string, string> = {};
    for (const o of (data as { owners: { clientId: string; email: string }[] }).owners) {
      map[o.clientId] = o.email;
    }
    setOwnerEmails(map);
  }

  async function loadLoyaltyFlags() {
    if (!supabase) return;
    const { data, error } = await supabase
      .from('loyalty_settings')
      .select('client_id, platform_enabled');
    if (error || !data) return;
    const map: LoyaltyFlags = {};
    for (const row of data as { client_id: string; platform_enabled: boolean }[]) {
      map[row.client_id] = row.platform_enabled;
    }
    setLoyaltyFlags(map);
  }

  async function loadPassCounts() {
    if (!supabase) return;
    const { data, error } = await supabase
      .from('loyalty_members')
      .select('client_id')
      .not('wallet_serial', 'is', null);
    if (error || !data) return;
    const map: PassCounts = {};
    for (const row of data as { client_id: string }[]) {
      map[row.client_id] = (map[row.client_id] ?? 0) + 1;
    }
    setPassCounts(map);
  }

  useEffect(() => {
    void load();
    void loadOwnerEmails();
    void loadPassCounts();
    void loadLoyaltyFlags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function createClient() {
    if (!supabase) return;
    if (!name.trim() || !ownerEmail.trim()) {
      setError(t('clients.required'));
      return;
    }
    setBusy(true);
    setError(null);
    setCreds(null);
    const { data, error } = await supabase.functions.invoke('create-client', {
      body: { name: name.trim(), ownerEmail: ownerEmail.trim(), ownerFullName: ownerName.trim() },
    });
    setBusy(false);
    if (error) {
      setError(error.message);
      return;
    }
    const result = data as { owner: { email: string; password: string }; client: { name: string } };
    setCreds({
      email: result.owner.email,
      password: result.owner.password,
      clientName: result.client.name,
    });
    setName('');
    setOwnerEmail('');
    setOwnerName('');
    void load();
    void loadOwnerEmails();
    void loadPassCounts();
    void loadLoyaltyFlags();
  }

  async function toggleLoyalty(c: ClientRow) {
    if (!supabase) return;
    setError(null);
    const enabling = !loyaltyFlags[c.id];
    const payload: Database['public']['Tables']['loyalty_settings']['Insert'] = {
      client_id: c.id,
      platform_enabled: enabling,
      pass_logo_text: c.name,
      updated_at: new Date().toISOString(),
      ...(enabling ? {} : { enabled: false }),
    };
    const { error: err } = await supabase.from('loyalty_settings').upsert(payload, {
      onConflict: 'client_id',
    });
    if (err) setError(err.message);
    else void loadLoyaltyFlags();
  }

  async function toggleTableService(c: ClientRow) {
    if (!supabase) return;
    setError(null);
    const { error: err } = await supabase
      .from('clients')
      .update({ table_service_enabled: !c.table_service_enabled })
      .eq('id', c.id);
    if (err) setError(err.message);
    else void load();
  }

  async function toggleBookings(c: ClientRow) {
    if (!supabase) return;
    setError(null);
    const { error: err } = await supabase
      .from('clients')
      .update({ bookings_enabled: !c.bookings_enabled })
      .eq('id', c.id);
    if (err) setError(err.message);
    else void load();
  }

  async function toggleActive(c: ClientRow) {
    if (!supabase) return;
    setError(null);
    const { error: err } = await supabase
      .from('clients')
      .update({ active: !c.active })
      .eq('id', c.id);
    if (err) setError(err.message);
    else void load();
  }

  async function saveRename(id: string) {
    if (!supabase || !editName.trim()) return;
    setError(null);
    const { error: err } = await supabase
      .from('clients')
      .update({ name: editName.trim() })
      .eq('id', id);
    if (err) setError(err.message);
    else {
      setEditId(null);
      setEditName('');
      void load();
    }
  }

  async function resetPassword(clientId: string) {
    if (!supabase) return;
    setResetState((prev) => ({
      ...prev,
      [clientId]: { busy: true, confirming: false, creds: null },
    }));
    const { data, error } = await supabase.functions.invoke('reset-client-password', {
      body: { clientId },
    });
    if (error || !data) {
      setResetState((prev) => ({
        ...prev,
        [clientId]: { busy: false, confirming: false, creds: null },
      }));
      setError(error?.message ?? t('clients.resetFailed'));
      return;
    }
    const result = data as { email: string; password: string };
    setResetState((prev) => ({
      ...prev,
      [clientId]: { busy: false, confirming: false, creds: result },
    }));
  }

  const activeClients = clients.filter((c) => c.active);
  const sellingToday = snapshot
    ? activeClients.filter((c) => (snapshot.stats[c.id]?.orders_today ?? 0) > 0).length
    : 0;
  const quietCafes = snapshot
    ? activeClients.filter((c) => {
        const state = freshness(snapshot.stats[c.id]?.last_order_at ?? null);
        if (state === 'quiet') return true;
        if (state === 'never') {
          const age = daysSince(c.created_at);
          return age !== null && age > 3;
        }
        return false;
      }).length
    : 0;
  const ordersToday = snapshot
    ? activeClients.reduce((sum, c) => sum + (snapshot.stats[c.id]?.orders_today ?? 0), 0)
    : 0;
  const force30 = snapshot
    ? activeClients.reduce((sum, c) => sum + (snapshot.stats[c.id]?.force_30d ?? 0), 0)
    : 0;
  const revenue30 = snapshot
    ? activeClients.reduce((sum, c) => sum + (snapshot.stats[c.id]?.revenue_30d ?? 0), 0)
    : 0;
  const statsClient = clients.find((c) => c.id === statsClientId) ?? null;

  function orderAgo(iso: string | null): string {
    const days = daysSince(iso);
    if (days === null) return t('clients.freshNever');
    if (days === 0) return t('clients.freshToday');
    if (days === 1) return t('clients.freshYesterday');
    return t('clients.freshDays', { n: days });
  }

  return (
    <section className="ui-stack">
      <PageHeader title={t('clients.title')} subtitle={t('clients.subtitle')} />

      {error && <Callout tone="danger">{error}</Callout>}

      {snapshot && (
        <Stats>
          <Stat
            label={t('clients.statsActiveToday')}
            value={sellingToday}
            sub={t('clients.statsActiveSub', { selling: sellingToday, total: activeClients.length })}
          />
          <Stat label={t('clients.statsOrdersToday')} value={ordersToday} />
          <Stat label={t('clients.statsQuiet')} value={quietCafes} sub={t('clients.statsQuietSub')} />
          <Stat label={t('clients.statsForce30')} value={force30} />
          <Stat label={t('clients.statsRevenue30')} value={formatAmd(revenue30, i18n.language)} />
        </Stats>
      )}

      <Card>
        <CardHeader title={t('clients.newTitle')} />
        <CardBody>
          <FormGrid>
            <Field label={t('clients.cafeName')} htmlFor="client-name">
              <input
                id="client-name"
                placeholder={t('clients.cafePlaceholder')}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
            <Field label={t('clients.ownerEmail')} htmlFor="client-owner-email">
              <input
                id="client-owner-email"
                placeholder={t('clients.ownerEmailPlaceholder')}
                type="email"
                value={ownerEmail}
                onChange={(e) => setOwnerEmail(e.target.value)}
              />
            </Field>
            <Field label={t('clients.ownerName')} htmlFor="client-owner-name" optional>
              <input
                id="client-owner-name"
                placeholder={t('clients.ownerNamePlaceholder')}
                value={ownerName}
                onChange={(e) => setOwnerName(e.target.value)}
              />
            </Field>
          </FormGrid>
          <div style={{ marginTop: 16 }}>
            <Button variant="primary" onClick={() => void createClient()} disabled={busy}>
              {busy ? t('common.creating') : t('clients.submit')}
            </Button>
          </div>
        </CardBody>
      </Card>

      {creds && (
        <Callout tone="success" title={t('clients.credsTitle', { name: creds.clientName })}>
          <p style={{ margin: '0 0 10px' }}>{t('clients.credsNote')}</p>
          <div className="creds-row">
            <span>{t('auth.email')}</span>
            <code>{creds.email}</code>
          </div>
          <div className="creds-row">
            <span>{t('auth.password')}</span>
            <code>{creds.password}</code>
          </div>
          <Button
            variant="secondary"
            size="sm"
            style={{ marginTop: 10 }}
            onClick={() =>
              navigator.clipboard.writeText(
                `${t('auth.email')}: ${creds.email}\n${t('auth.password')}: ${creds.password}`,
              )
            }
          >
            {t('clients.copyCreds')}
          </Button>
        </Callout>
      )}

      <Card layout="flush">
        <CardHeader title={t('clients.allClients')} />
        {loading ? (
          <CardBody>
            <p className="muted">{t('common.loading')}</p>
          </CardBody>
        ) : clients.length === 0 ? (
          <EmptyState title={t('clients.emptyTitle')} text={t('clients.empty')} />
        ) : (
          <div className="table-scroll">
          <table className="table clients-table">
          <thead>
            <tr>
              <th>{t('clients.colCafe')}</th>
              <th className="clients-col-secondary">{t('clients.colOwnerEmail')}</th>
              <th>{t('clients.colLoyalty')}</th>
              <th>{t('clients.colTableService')}</th>
              <th>{t('clients.colBookings')}</th>
              <th className="clients-col-secondary">{t('clients.colWalletPasses')}</th>
              <th>{t('clients.colActive')}</th>
              <th className="clients-col-secondary">{t('clients.colCreated')}</th>
              <th>{t('clients.colLastOrder')}</th>
              <th>{t('clients.colOrders30')}</th>
              <th>{t('clients.colRegisters')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => {
              const rs = resetState[c.id];
              const stat = snapshot?.stats[c.id];
              const devices = snapshot?.devices[c.id] ?? [];
              const registers = registerSummary(devices);
              const fresh = freshness(stat?.last_order_at ?? null);
              const freshTone =
                fresh === 'today' ? 'success' : fresh === 'recent' ? 'warning' : fresh === 'quiet' ? 'danger' : 'neutral';
              const versionText = !registers
                ? t('clients.versionNone')
                : registers.versions.length > 1
                  ? registers.versions.join(' · ')
                  : registers.count > 1
                    ? t('clients.versionCount', { version: registers.versions[0], n: registers.count })
                    : registers.versions[0];
              return (
                <React.Fragment key={c.id}>
                  <tr style={c.active ? undefined : { opacity: 0.6 }}>
                    <td>
                      {editId === c.id ? (
                        <span style={{ display: 'inline-flex', gap: 6 }}>
                          <input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && void saveRename(c.id)}
                            autoFocus
                          />
                          <button className="btn btn-sm" onClick={() => void saveRename(c.id)}>
                            Save
                          </button>
                          <button
                            className="btn-ghost btn-sm"
                            onClick={() => {
                              setEditId(null);
                              setEditName('');
                            }}
                          >
                            ×
                          </button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="link"
                          title={t('clients.openStats')}
                          onClick={() => setStatsClientId(c.id)}
                        >
                          {c.name}
                        </button>
                      )}
                    </td>
                    <td className="muted clients-col-secondary" style={{ fontSize: 13 }}>
                      {ownerEmails[c.id] ?? '—'}
                    </td>
                    <td>
                      <button
                        className="link"
                        onClick={() => void toggleLoyalty(c)}
                        title={t('clients.colLoyalty')}
                        style={{ color: loyaltyFlags[c.id] ? 'var(--accent)' : 'var(--text-muted)' }}
                      >
                        {loyaltyFlags[c.id] ? t('common.on') : t('common.off')}
                      </button>
                    </td>
                    <td>
                      <button
                        className="link"
                        onClick={() => void toggleTableService(c)}
                        title={t('clients.colTableService')}
                        style={{
                          color: c.table_service_enabled ? 'var(--accent)' : 'var(--text-muted)',
                        }}
                      >
                        {c.table_service_enabled ? t('common.on') : t('common.off')}
                      </button>
                    </td>
                    <td>
                      <button
                        className="link"
                        onClick={() => void toggleBookings(c)}
                        title={t('clients.colBookings')}
                        style={{
                          color: c.bookings_enabled ? 'var(--accent)' : 'var(--text-muted)',
                        }}
                      >
                        {c.bookings_enabled ? t('common.on') : t('common.off')}
                      </button>
                    </td>
                    <td className="clients-col-secondary">{passCounts[c.id] ?? 0}</td>
                    <td>
                      <button className="link" onClick={() => void toggleActive(c)} title={t('clients.colActive')}>
                        <StatusPill status={c.active ? 'active' : 'inactive'}>
                          {c.active ? t('common.active') : t('common.inactive')}
                        </StatusPill>
                      </button>
                    </td>
                    <td className="clients-col-secondary">{new Date(c.created_at).toLocaleDateString()}</td>
                    <td>
                      {snapshot ? (
                        <StatusPill status={fresh} tone={freshTone}>
                          {orderAgo(stat?.last_order_at ?? null)}
                        </StatusPill>
                      ) : (
                        t('common.emptyDash')
                      )}
                    </td>
                    <td>{stat?.orders_30d ?? t('common.emptyDash')}</td>
                    <td>
                      {snapshot ? (
                        <span className={registers?.stale || (registers && registers.versions.length > 1) ? 'client-stat-alert' : undefined}>
                          {versionText}
                        </span>
                      ) : (
                        t('common.emptyDash')
                      )}
                    </td>
                    <td className="clients-row-menu">
                      <RowMenu
                        label={t('clients.rowActions')}
                        items={[
                          {
                            label: t('clients.rename'),
                            icon: <Pencil className="ui-i" />,
                            onSelect: () => {
                              setEditId(c.id);
                              setEditName(c.name);
                            },
                          },
                          {
                            label: t('common.newPassword'),
                            icon: <KeyRound className="ui-i" />,
                            disabled: rs?.busy || rs?.confirming || !!rs?.creds,
                            onSelect: () =>
                              setResetState((prev) => ({
                                ...prev,
                                [c.id]: { busy: false, confirming: true, creds: null },
                              })),
                          },
                        ]}
                      />
                    </td>
                  </tr>
                  {rs?.confirming && (
                    <tr>
                      <td
                        colSpan={12}
                        style={{
                          padding: '10px 14px',
                          background: 'var(--surface-raised)',
                          borderTop: 'none',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <span style={{ fontSize: 14 }}>
                            {t('clients.resetConfirm', { name: c.name })}
                          </span>
                          <button
                            className="btn btn-sm"
                            style={{
                              background: 'var(--danger, #dc2626)',
                              color: '#fff',
                              borderColor: 'var(--danger, #dc2626)',
                              whiteSpace: 'nowrap',
                              flexShrink: 0,
                            }}
                            disabled={rs.busy}
                            onClick={() => void resetPassword(c.id)}
                          >
                            {rs.busy ? '…' : t('clients.resetYes')}
                          </button>
                          <button
                            className="btn-ghost btn-sm"
                            style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                            onClick={() =>
                              setResetState((prev) => ({
                                ...prev,
                                [c.id]: { busy: false, confirming: false, creds: null },
                              }))
                            }
                          >
                            {t('common.cancel')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                  {rs?.creds && (
                    <tr>
                      <td
                        colSpan={12}
                        style={{
                          padding: '10px 14px',
                          background: 'var(--surface-raised)',
                          borderTop: 'none',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 16,
                            flexWrap: 'wrap',
                          }}
                        >
                          <span style={{ fontSize: 13, fontWeight: 600 }}>
                            {t('clients.credsTitle', { name: c.name })}
                          </span>
                          <span style={{ fontSize: 13 }}>
                            <span className="muted">{t('auth.email')} </span>
                            <code>{rs.creds.email}</code>
                          </span>
                          <span style={{ fontSize: 13 }}>
                            <span className="muted">{t('auth.password')} </span>
                            <code>{rs.creds.password}</code>
                          </span>
                          <button
                            className="btn btn-sm"
                            onClick={() =>
                              navigator.clipboard.writeText(
                                `${t('auth.email')}: ${rs.creds!.email}\n${t('auth.password')}: ${rs.creds!.password}`,
                              )
                            }
                          >
                            {t('common.copy')}
                          </button>
                          <button
                            className="link"
                            style={{ color: 'var(--text-muted)', fontSize: 13 }}
                            onClick={() =>
                              setResetState((prev) => ({
                                ...prev,
                                [c.id]: { busy: false, confirming: false, creds: null },
                              }))
                            }
                          >
                            {t('common.dismiss')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
          </div>
        )}
      </Card>
      {statsClient && (
        <ClientStatsSheet
          name={statsClient.name}
          stat={snapshot?.stats[statsClient.id]}
          devices={snapshot?.devices[statsClient.id] ?? []}
          offlineMinutes30d={snapshot?.offlineMinutes30d ?? {}}
          diagnostics30d={snapshot?.diagnostics30d[statsClient.id] ?? 0}
          onClose={() => setStatsClientId(null)}
        />
      )}
    </section>
  );
}
